import { usePackContext } from "domains/pack";
import { packsClient } from "lib/clients";
import { encrypt } from "lib/e2ee";
import { FieldTypes } from "lib/types";
import { cleanUrl, getStringType, retryCommand, urlToContentType } from "lib/utils";
import { useNotificaitons, usePack, useRunSync, useToastNotification } from ".";

export const useCreateAsset = () => {
  const { packId } = usePackContext();
  const { data: pack } = usePack(packId);
  const { sync } = useRunSync(packId);
  const { storageLimitNotification, assetLimitNotification } = useNotificaitons();
  const { errorToast, successToast } = useToastNotification();

  const send = async (type: string, data: any, promptId?: string) => {
    const asset = {
      type
    } as any;

    try {
      if (pack.is_encrypted) {
        asset.enc_data = await encrypt(data, pack.key);
      }
      else {
        asset.data = data;
      }

      if (promptId) {
        asset.prompt_id = promptId
      }

      await packsClient.createAsset(packId, asset);

      await sync();
      successToast({ title: 'Success', description: 'Your asset has been created.' });
    } catch (err) {
      console.log(err);

      if (err.response?.status === 507) {
        storageLimitNotification();
        return;
      }
      if (err.response?.status === 402) {
        retryCommand(packId, asset, sync);
        assetLimitNotification();
        return;
      }
      errorToast({ title: 'Error', description: 'Could not create asset, please try again.' });
    }
  }

  const text = async (value: string, promptId?: string) => {
    await send('text', { value }, promptId);
  }

  const link = async (url: string, title?: string, promptId?: string) => {
    url = cleanUrl(url);

    const data = { url } as any;

    if (title) {
      data.title = title;
    }

    const contentType = urlToContentType(url);

    if (contentType) {
        data.content_type = contentType;
    }

    await send('link', data, promptId);
  }

  const html = async (value: string, promptId?: string) => {
    await send('html', { value }, promptId);
  }

  const unknown = async (value: string, promptId?: string) => {
    const command = getStringType(value);

    if (command === FieldTypes.LINK) {
      await link(value, null, promptId);
    }
    else {
      await send(command, { value }, promptId);
    }
  }

  const prompt = async (prompt_for: string) => {
    await send('prompt', { prompt_for });
  }

  return {
    link,
    text,
    html,
    unknown,
    prompt
  }
}